import { useLingui } from "@lingui/react/macro";
import { memo, type PointerEventHandler } from "react";
import { Spinner } from "../ui/spinner";
import { ComposerVoiceButton, type ComposerVoiceInputControls } from "./ComposerVoiceButton";

interface ComposerPrimaryActionsProps {
  isRunning: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  isBlocked: boolean;
  isUploading: boolean;
  hasSendableContent: boolean;
  voiceInput?: ComposerVoiceInputControls | null;
  preserveComposerFocusOnPointerDown?: boolean;
  onInterrupt: () => void;
}

const preventPointerFocus: PointerEventHandler<HTMLElement> = (event) => {
  event.preventDefault();
};

export const ComposerPrimaryActions = memo(function ComposerPrimaryActions({
  isRunning,
  isSendBusy,
  isConnecting,
  isEnvironmentUnavailable,
  isBlocked,
  isUploading,
  hasSendableContent,
  voiceInput = null,
  preserveComposerFocusOnPointerDown = false,
  onInterrupt,
}: ComposerPrimaryActionsProps) {
  const { t } = useLingui();
  const pointerFocusProps = preserveComposerFocusOnPointerDown
    ? { onPointerDown: preventPointerFocus }
    : undefined;
  const voiceRecording = voiceInput?.status === "recording";
  const voiceButton =
    voiceInput && (voiceRecording || !voiceInput.hasPendingClips) ? (
      <ComposerVoiceButton
        {...voiceInput}
        preserveComposerFocusOnPointerDown={preserveComposerFocusOnPointerDown}
      />
    ) : null;

  if (isUploading || (isRunning && !hasSendableContent)) {
    return (
      <>
        {voiceButton}
        <button
          type="button"
          className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-destructive/90 text-white shadow-xs shadow-destructive/24 inset-shadow-[0_1px_--theme(--color-white/16%)] transition-all duration-150 hover:bg-destructive hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none sm:h-8 sm:w-8"
          {...pointerFocusProps}
          onClick={onInterrupt}
          aria-label={isUploading ? t`Stop upload` : t`Stop generation`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <rect x="2" y="2" width="8" height="8" rx="1.5" />
          </svg>
        </button>
      </>
    );
  }

  const voiceBusy = voiceInput?.status === "finishing" || voiceInput?.status === "requesting";
  const hasVoiceContent = voiceRecording || voiceInput?.hasPendingClips === true;
  return (
    <>
      {voiceButton}
      <button
        type="submit"
        className="flex h-9 w-9 enabled:cursor-pointer items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-xs enabled:shadow-primary/24 enabled:inset-shadow-[0_1px_--theme(--color-white/16%)] transition-all duration-150 hover:bg-primary hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none disabled:pointer-events-none disabled:opacity-30 disabled:shadow-none disabled:hover:scale-100 sm:h-8 sm:w-8"
        {...pointerFocusProps}
        disabled={
          isSendBusy ||
          isConnecting ||
          isEnvironmentUnavailable ||
          isBlocked ||
          voiceBusy ||
          (!hasSendableContent && !hasVoiceContent)
        }
        aria-label={
          isBlocked
            ? t`Chat blocked`
            : isEnvironmentUnavailable
              ? t`Environment disconnected`
              : isConnecting
                ? t`Connecting`
                : isSendBusy
                  ? t`Sending`
                  : t`Send message`
        }
      >
        {isConnecting || isSendBusy ? (
          <Spinner className="size-3.5" aria-hidden="true" />
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </>
  );
});
